import {
  Button,
  Checkbox,
  Fieldset,
  Group,
  Modal,
  NumberInput,
  Radio,
  SegmentedControl,
  Select,
  Slider,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconBuilding,
  IconDiscount2,
  IconHome,
  IconInfoCircle,
  IconReceipt2,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TitleDescriptionRadio } from 'src/Components/Shared/TitleDescriptionRadio';
import { useUpdateGovernance } from 'src/Hooks/Mutations/useUpdateGovernance';
import {
  LegalStructure,
  ManagementMode,
  RentalDeclarationRegime,
  ShareType,
  SpecialStatus,
  SyndicLibreType,
  TaxRegime,
  UpdateGovernanceDto,
} from 'src/types/governance.types';

import styles from './GovernanceSetupModal.module.scss';
import { Props } from './GovernanceSetupModal.types';

const LEGAL_STRUCTURE_OPTIONS = [
  {
    value: LegalStructure.FULL_OWNERSHIP,
    title: 'Pleine Propriété',
    description: 'Seul ou en couple',
    icon: IconHome,
  },
  {
    value: LegalStructure.INDIVISION,
    title: 'Indivision',
    description: 'Héritage partagé, sans société',
    icon: IconUsersGroup,
  },
  {
    value: LegalStructure.SCI,
    title: 'SCI',
    description: 'Société Civile Immobilière',
    icon: IconBuilding,
  },
  {
    value: LegalStructure.SARL_FAMILLE,
    title: 'SARL de Famille',
    description: 'Société familiale',
    icon: IconUsers,
  },
];

const TAX_REGIME_OPTIONS = [
  { value: TaxRegime.IR, label: 'Impôt sur le Revenu (IR) - Le plus courant' },
  { value: TaxRegime.IS, label: 'Impôt sur les Sociétés (IS)' },
];

const RENTAL_DECLARATION_REGIME_OPTIONS = [
  {
    value: RentalDeclarationRegime.REEL,
    title: 'Régime réel',
    description: 'Déduction des charges réelles (formulaire 2044)',
    icon: IconReceipt2,
  },
  {
    value: RentalDeclarationRegime.MICRO_FONCIER,
    title: 'Micro-foncier',
    description: 'Abattement forfaitaire de 30%, pas de ventilation de charges',
    icon: IconDiscount2,
  },
];

interface FormValues {
  legalStructure: LegalStructure | null;
  taxRegime: TaxRegime | null;
  specialStatuses: SpecialStatus[];
  hasDismemberment: boolean;
  shareType: ShareType | null;
  totalShares: number | null;
  managementMode: ManagementMode | null;
  syndicLibreType: SyndicLibreType | null;
  rentalVentilationEnabled: boolean;
  rentalDeclarationRegime: RentalDeclarationRegime | null;
  defaultLocativeShare: number | null;
}

const MANAGEMENT_MODE_OPTIONS = [
  { value: ManagementMode.SYNDIC_PRO, label: '🏢 Syndic professionnel' },
  { value: ManagementMode.SYNDIC_LIBRE, label: '🤝 Syndic libre (géré par les copropriétaires)' },
  { value: ManagementMode.NO_COPRO, label: '🏠 Pas de copropriété / Maison individuelle' },
  { value: ManagementMode.UNKNOWN, label: '❓ Je ne sais pas' },
];

const SYNDIC_LIBRE_OPTIONS = [
  {
    value: SyndicLibreType.BENEVOLE,
    title: 'Syndic bénévole',
    description: "Un copropriétaire élu gère l'immeuble",
  },
  {
    value: SyndicLibreType.COOPERATIF,
    title: 'Syndic coopératif',
    description: 'Le conseil syndical assure la gestion collégialement',
  },
  {
    value: SyndicLibreType.UNKNOWN,
    title: 'Je ne sais pas',
    description: 'Pas sûr ? Pas de souci, vous pourrez modifier plus tard',
  },
];

const GovernanceSetupModal = ({ open, onClose, governance }: Props) => {
  const { t } = useTranslation();
  const { mutate: updateGovernance, isLoading } = useUpdateGovernance();

  const form = useForm<FormValues>({
    initialValues: {
      legalStructure: null,
      taxRegime: null,
      specialStatuses: [],
      hasDismemberment: false,
      shareType: null,
      totalShares: null,
      managementMode: null,
      syndicLibreType: null,
      rentalVentilationEnabled: false,
      rentalDeclarationRegime: null,
      defaultLocativeShare: null,
    },
  });

  useEffect(() => {
    if (open && governance) {
      form.setValues({
        legalStructure: governance.legalStructure ?? null,
        taxRegime: governance.taxRegime ?? null,
        specialStatuses: governance.specialStatuses ?? [],
        hasDismemberment: governance.hasDismemberment ?? false,
        shareType: governance.shareType ?? null,
        totalShares: governance.totalShares ?? null,
        managementMode: governance.managementMode ?? null,
        syndicLibreType: governance.syndicLibreType ?? null,
        rentalVentilationEnabled: !!governance.rentalDeclarationRegime,
        rentalDeclarationRegime: governance.rentalDeclarationRegime ?? null,
        defaultLocativeShare: governance.defaultLocativeShare ?? null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isSociete =
    form.values.legalStructure === LegalStructure.SCI ||
    form.values.legalStructure === LegalStructure.SARL_FAMILLE;

  const showShareConfig =
    form.values.legalStructure && form.values.legalStructure !== LegalStructure.FULL_OWNERSHIP;

  const handleStructureChange = (value: string) => {
    const structure = value as LegalStructure;
    form.setFieldValue('legalStructure', structure);
    if (structure === LegalStructure.INDIVISION) {
      form.setFieldValue('shareType', ShareType.PERCENTAGE);
    } else if (structure === LegalStructure.SCI || structure === LegalStructure.SARL_FAMILLE) {
      form.setFieldValue('shareType', ShareType.PARTS_SOCIALES);
    } else {
      form.setFieldValue('shareType', null);
      form.setFieldValue('totalShares', null);
    }
  };

  const handleSave = () => {
    const isRentalVentilationReel =
      form.values.rentalVentilationEnabled &&
      form.values.rentalDeclarationRegime === RentalDeclarationRegime.REEL;

    const dto: UpdateGovernanceDto = {
      legalStructure: form.values.legalStructure,
      taxRegime: isSociete ? form.values.taxRegime : null,
      specialStatuses: form.values.specialStatuses.length > 0 ? form.values.specialStatuses : null,
      hasDismemberment: form.values.hasDismemberment,
      shareType: form.values.shareType,
      totalShares: form.values.shareType === ShareType.PERCENTAGE ? null : form.values.totalShares,
      managementMode: form.values.managementMode,
      syndicLibreType:
        form.values.managementMode === ManagementMode.SYNDIC_LIBRE
          ? form.values.syndicLibreType
          : null,
      rentalDeclarationRegime: form.values.rentalVentilationEnabled
        ? form.values.rentalDeclarationRegime
        : null,
      defaultLocativeShare: isRentalVentilationReel ? form.values.defaultLocativeShare : null,
    };

    updateGovernance(dto, {
      onSuccess: () => onClose(),
    });
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={t('admin.governance.title', 'Gouvernance juridique')}
      size="xl"
    >
      <div className={styles.form}>
        <Fieldset
          legend={
            <Group gap="xs">
              <span>{t('admin.governance.managementModeLabel', 'Mode de gestion')}</span>
              <Tooltip
                label={t(
                  'admin.governance.managementModeTooltip',
                  "Le mode de gestion désigne qui s'occupe de l'entretien des parties communes de votre immeuble : un cabinet professionnel ou les copropriétaires eux-mêmes."
                )}
                multiline
                w={320}
              >
                <IconInfoCircle
                  size={16}
                  style={{ cursor: 'help', color: 'var(--mantine-color-gray-5)' }}
                />
              </Tooltip>
            </Group>
          }
        >
          <Select
            data={MANAGEMENT_MODE_OPTIONS}
            value={form.values.managementMode}
            placeholder={t(
              'admin.governance.managementModePlaceholder',
              'Sélectionnez votre mode de gestion'
            )}
            onChange={value => {
              const mode = (value as ManagementMode) ?? null;
              form.setFieldValue('managementMode', mode);
              if (mode !== ManagementMode.SYNDIC_LIBRE) {
                form.setFieldValue('syndicLibreType', null);
              }
            }}
          />

          {form.values.managementMode === ManagementMode.SYNDIC_LIBRE && (
            <div className={styles.syndicLibreBlock}>
              <div className={styles.syndicLibreIntro}>
                <Text size="sm" fw={600}>
                  💡 {t('admin.governance.syndicLibreIntroTitle', 'Pour aller plus loin')}{' '}
                  <Text span c="dimmed" fw={400}>
                    {t('admin.governance.syndicLibreIntroOptional', '(optionnel)')}
                  </Text>
                </Text>
                <Text size="sm" c="dimmed">
                  {t(
                    'admin.governance.syndicLibreIntroDescription',
                    'Précisez le type de syndic libre pour valoriser encore mieux votre bien.'
                  )}
                </Text>
              </div>

              <Group gap="xs" mt="md">
                <Text size="sm" fw={500}>
                  {t('admin.governance.syndicLibreTypeLabel', 'Type de syndic libre')}
                </Text>
                <Tooltip
                  label={t(
                    'admin.governance.syndicLibreTypeTooltip',
                    "Ces deux modes de gestion sont reconnus par la loi du 10 juillet 1965. Le syndic bénévole est assuré par un seul copropriétaire élu, tandis que le syndic coopératif implique l'ensemble du conseil syndical."
                  )}
                  multiline
                  w={320}
                >
                  <IconInfoCircle
                    size={16}
                    style={{ cursor: 'help', color: 'var(--mantine-color-gray-5)' }}
                  />
                </Tooltip>
              </Group>

              <Radio.Group
                value={form.values.syndicLibreType ?? ''}
                onChange={value => form.setFieldValue('syndicLibreType', value as SyndicLibreType)}
                mt="xs"
              >
                <Stack gap="xs">
                  {SYNDIC_LIBRE_OPTIONS.map(option => (
                    <Radio
                      key={option.value}
                      value={option.value}
                      label={
                        <div>
                          <Text size="sm" fw={500}>
                            {option.title}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {option.description}
                          </Text>
                        </div>
                      }
                    />
                  ))}
                </Stack>
              </Radio.Group>
            </div>
          )}
        </Fieldset>

        <Fieldset legend={t('admin.governance.selectStructure', 'Structure de detention')}>
          <Radio.Group value={form.values.legalStructure ?? ''} onChange={handleStructureChange}>
            <div className={styles.radioGrid}>
              {LEGAL_STRUCTURE_OPTIONS.map(option => (
                <TitleDescriptionRadio
                  key={option.value}
                  value={option.value}
                  title={option.title}
                  description={option.description}
                  illustrationIcon={option.icon}
                />
              ))}
            </div>
          </Radio.Group>
        </Fieldset>

        {isSociete && (
          <div className={styles.conditionalSection}>
            <Fieldset legend={t('admin.governance.taxRegimeLabel', 'Régime fiscal')}>
              <Select
                data={TAX_REGIME_OPTIONS}
                value={form.values.taxRegime}
                onChange={value => form.setFieldValue('taxRegime', value as TaxRegime)}
                placeholder={t('admin.governance.selectTaxRegime', 'Choisir un régime')}
              />
            </Fieldset>
          </div>
        )}

        {form.values.legalStructure && (
          <div className={styles.conditionalSection}>
            <Fieldset legend={t('admin.governance.optionsLegend', 'Options et statuts')}>
              <div className={styles.fieldsetContent}>
                <div>
                  <Text size="sm" fw={500} mb="sm">
                    {t(
                      'admin.governance.specialStatusLabel',
                      "Bénéficiez-vous d'un statut particulier ?"
                    )}
                  </Text>
                  <Checkbox.Group
                    value={form.values.specialStatuses}
                    onChange={value =>
                      form.setFieldValue('specialStatuses', value as SpecialStatus[])
                    }
                  >
                    <Group>
                      <Checkbox value={SpecialStatus.LMNP} label="Location Meublée (LMNP)" />
                      <Checkbox
                        value={SpecialStatus.MONUMENT_HISTORIQUE}
                        label="Monument Historique"
                      />
                    </Group>
                  </Checkbox.Group>
                </div>

                <Group gap="xs">
                  <Checkbox
                    label={t(
                      'admin.governance.dismembermentLabel',
                      'Il y a un démembrement de propriété dans la famille'
                    )}
                    checked={form.values.hasDismemberment}
                    onChange={e => form.setFieldValue('hasDismemberment', e.currentTarget.checked)}
                  />
                  <Tooltip
                    label="Certains membres ont l'Usufruit (le droit d'usage) et d'autres la Nue-propriété (les murs ou les parts)."
                    multiline
                    w={300}
                  >
                    <IconInfoCircle
                      size={16}
                      style={{
                        cursor: 'help',
                        color: 'var(--mantine-color-gray-5)',
                      }}
                    />
                  </Tooltip>
                </Group>
              </div>
            </Fieldset>
          </div>
        )}

        <Fieldset legend={t('admin.governance.rentalVentilationLegend', 'Fiscalité locative')}>
          <div className={styles.fieldsetContent}>
            <Checkbox
              label={t(
                'admin.governance.rentalVentilationToggle',
                'Activer la ventilation locative / usage personnel des dépenses'
              )}
              checked={form.values.rentalVentilationEnabled}
              onChange={e => {
                const checked = e.currentTarget.checked;
                form.setFieldValue('rentalVentilationEnabled', checked);
                if (!checked) {
                  form.setFieldValue('rentalDeclarationRegime', null);
                  form.setFieldValue('defaultLocativeShare', null);
                }
              }}
            />

            {form.values.rentalVentilationEnabled && (
              <div className={styles.conditionalSection}>
                <Radio.Group
                  value={form.values.rentalDeclarationRegime ?? ''}
                  onChange={value =>
                    form.setFieldValue('rentalDeclarationRegime', value as RentalDeclarationRegime)
                  }
                >
                  <div className={styles.radioGrid}>
                    {RENTAL_DECLARATION_REGIME_OPTIONS.map(option => (
                      <TitleDescriptionRadio
                        key={option.value}
                        value={option.value}
                        title={option.title}
                        description={option.description}
                        illustrationIcon={option.icon}
                      />
                    ))}
                  </div>
                </Radio.Group>

                {form.values.rentalDeclarationRegime === RentalDeclarationRegime.REEL && (
                  <div style={{ marginTop: 'var(--mantine-spacing-md)' }}>
                    <Text size="sm" fw={500} mb="xs">
                      {t(
                        'admin.governance.defaultLocativeShareLabel',
                        'Part locative par défaut sur les nouvelles dépenses'
                      )}
                    </Text>
                    <Slider
                      value={form.values.defaultLocativeShare ?? 0}
                      onChange={value => form.setFieldValue('defaultLocativeShare', value)}
                      min={0}
                      max={100}
                      label={percent => `${percent}%`}
                    />
                  </div>
                )}

                {form.values.rentalDeclarationRegime === RentalDeclarationRegime.MICRO_FONCIER && (
                  <Text size="sm" c="dimmed" mt="xs">
                    {t(
                      'admin.governance.microFoncierInfo',
                      "En micro-foncier, un abattement forfaitaire de 30% s'applique déjà : la ventilation par dépense n'est pas nécessaire."
                    )}
                  </Text>
                )}
              </div>
            )}
          </div>
        </Fieldset>

        {showShareConfig && (
          <div className={styles.conditionalSection}>
            <Fieldset legend={t('admin.governance.sharesLegend', 'Répartition des parts')}>
              <div className={styles.fieldsetContent}>
                {form.values.legalStructure === LegalStructure.INDIVISION && (
                  <SegmentedControl
                    data={[
                      { value: ShareType.PERCENTAGE, label: 'Pourcentages (%)' },
                      { value: ShareType.TANTIEMES, label: 'Tantièmes' },
                    ]}
                    value={form.values.shareType ?? ShareType.PERCENTAGE}
                    onChange={value => {
                      form.setFieldValue('shareType', value as ShareType);
                      if (value === ShareType.PERCENTAGE) {
                        form.setFieldValue('totalShares', null);
                      }
                    }}
                  />
                )}

                {form.values.shareType === ShareType.TANTIEMES && (
                  <NumberInput
                    label={t('admin.governance.totalTantiemes', 'Nombre total de tantièmes')}
                    value={form.values.totalShares ?? ''}
                    onChange={value =>
                      form.setFieldValue('totalShares', typeof value === 'number' ? value : null)
                    }
                    placeholder="ex: 1000"
                    min={1}
                    style={{ maxWidth: 450 }}
                  />
                )}

                {form.values.shareType === ShareType.PARTS_SOCIALES && (
                  <NumberInput
                    label={t(
                      'admin.governance.totalParts',
                      'Nombre total de parts sociales (selon vos statuts)'
                    )}
                    value={form.values.totalShares ?? ''}
                    onChange={value =>
                      form.setFieldValue('totalShares', typeof value === 'number' ? value : null)
                    }
                    placeholder="ex: 100"
                    min={1}
                    style={{ maxWidth: 450 }}
                  />
                )}
              </div>
            </Fieldset>
          </div>
        )}
      </div>

      <div className={styles.buttons}>
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button onClick={handleSave} loading={isLoading} disabled={!form.values.legalStructure}>
          {t('common.save', 'Enregistrer')}
        </Button>
      </div>
    </Modal>
  );
};

export default GovernanceSetupModal;
